import type { MatchRow, MatchWeapon } from "../api";

export interface MatchAggregate {
  rows: number;
  observedMatches: number;
  activeSeconds: number;
  kills: number;
  deaths: number;
  assists: number;
  wins: number;
  losses: number;
  score: number;
  damage: number;
  headshots: number;
  mergedRows: number;
  fallbackRows: number;
  weaponRows: number;
  kd: number | null;
  killsPerMinute: number | null;
  scorePerMinute: number | null;
  damagePerMatch: number | null;
  avgKillsPerMatch: number | null;
  winRate: number | null;
  topWeapon: AggregateWeapon | null;
  topGamemode: AggregateGamemode | null;
}

export interface AggregateWeapon {
  id: string;
  name: string;
  kills: number;
  damage: number;
  windows: number;
}

export interface AggregateGamemode {
  name: string;
  windows: number;
  observedMatches: number;
}

export function aggregateMatches(rows: MatchRow[]): MatchAggregate {
  const weapons = new Map<string, AggregateWeapon>();
  const gamemodes = new Map<string, AggregateGamemode>();
  const aggregate: MatchAggregate = {
    rows: rows.length,
    observedMatches: 0,
    activeSeconds: 0,
    kills: 0,
    deaths: 0,
    assists: 0,
    wins: 0,
    losses: 0,
    score: 0,
    damage: 0,
    headshots: 0,
    mergedRows: 0,
    fallbackRows: 0,
    weaponRows: 0,
    kd: null,
    killsPerMinute: null,
    scorePerMinute: null,
    damagePerMatch: null,
    avgKillsPerMatch: null,
    winRate: null,
    topWeapon: null,
    topGamemode: null,
  };

  for (const row of rows) {
    aggregate.observedMatches += row.merged_match_count;
    aggregate.activeSeconds += row.seconds_played_delta;
    aggregate.kills += row.kills_delta;
    aggregate.deaths += row.deaths_delta;
    aggregate.assists += row.assists_delta ?? 0;
    aggregate.wins += row.wins_delta;
    aggregate.losses += row.losses_delta;
    aggregate.score += row.score_delta;
    aggregate.damage += row.damage_delta;
    aggregate.headshots += row.headshots_delta;
    if (row.merged_match_count > 1 || row.derivation_kind === "merged") aggregate.mergedRows++;
    if (row.derivation_kind === "fallback") aggregate.fallbackRows++;
    if ((row.weapons ?? []).length > 0) aggregate.weaponRows++;
    if (row.gamemode_hint) addGamemode(gamemodes, row.gamemode_hint, row.merged_match_count);
    for (const weapon of row.weapons ?? []) addWeapon(weapons, weapon);
  }

  const minutes = aggregate.activeSeconds / 60;
  const decided = aggregate.wins + aggregate.losses;
  aggregate.kd = aggregate.deaths === 0 ? aggregate.kills : aggregate.kills / aggregate.deaths;
  aggregate.killsPerMinute = minutes <= 0 ? null : aggregate.kills / minutes;
  aggregate.scorePerMinute = minutes <= 0 ? null : aggregate.score / minutes;
  aggregate.damagePerMatch = aggregate.observedMatches <= 0 ? null : aggregate.damage / aggregate.observedMatches;
  aggregate.avgKillsPerMatch = aggregate.observedMatches <= 0 ? null : aggregate.kills / aggregate.observedMatches;
  aggregate.winRate = decided <= 0 ? null : aggregate.wins / decided;
  aggregate.topWeapon = [...weapons.values()].sort((a, b) => b.kills - a.kills || b.damage - a.damage || a.name.localeCompare(b.name))[0] ?? null;
  aggregate.topGamemode =
    [...gamemodes.values()].sort((a, b) => b.observedMatches - a.observedMatches || b.windows - a.windows || a.name.localeCompare(b.name))[0] ?? null;
  return aggregate;
}

export function rangeRows(rows: MatchRow[], from: number, to: number): MatchRow[] {
  return rows.filter((row) => row.window_end >= from && row.window_end < to);
}

export interface DailyBucket {
  day: string;
  start: number;
  end: number;
  aggregate: MatchAggregate;
}

const DAY_SECONDS = 86_400;

export function bucketByDay(rows: MatchRow[], days: number, nowSeconds = Math.floor(Date.now() / 1000)): DailyBucket[] {
  const startOfDay = (unix: number) => {
    const d = new Date(unix * 1000);
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  };
  const todayStart = startOfDay(nowSeconds);
  const buckets: DailyBucket[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const start = todayStart - i * DAY_SECONDS;
    const end = start + DAY_SECONDS;
    const dayRows = rows.filter((r) => r.window_end >= start && r.window_end < end);
    const d = new Date(start * 1000);
    const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    buckets.push({ day, start, end, aggregate: aggregateMatches(dayRows) });
  }
  return buckets;
}


function addWeapon(weapons: Map<string, AggregateWeapon>, weapon: MatchWeapon) {
  const current = weapons.get(weapon.id) ?? { id: weapon.id, name: weapon.name, kills: 0, damage: 0, windows: 0 };
  current.name = weapon.name;
  current.kills += weapon.kills_delta;
  current.damage += weapon.damage_delta;
  current.windows++;
  weapons.set(weapon.id, current);
}

function addGamemode(gamemodes: Map<string, AggregateGamemode>, name: string, observedMatches: number) {
  const current = gamemodes.get(name) ?? { name, windows: 0, observedMatches: 0 };
  current.windows++;
  current.observedMatches += observedMatches;
  gamemodes.set(name, current);
}
